import { useMemo, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CHEM_VESSELS, CHEM_SAMPLES, CHEM_HSE_KPIS, CHEM_VETTING } from "@/data/chemCarriageData";
import { InteractiveTankPlan } from "@/components/chem/InteractiveTankPlan";
import {
  Sparkles, FileText, Download, CheckCircle2, MapPin, MessageSquare,
  Leaf, QrCode, FlaskConical, Wind, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "welcome", label: "01 · Welcome" },
  { id: "fleet", label: "02 · My Fleet" },
  { id: "voyages", label: "03 · Voyages" },
  { id: "compat", label: "04 · Compatibility" },
  { id: "ledger", label: "05 · Tank History (L3C)" },
  { id: "tracking", label: "06 · Live Tracking" },
  { id: "samples", label: "07 · Sampling" },
  { id: "readiness", label: "08 · Pre-Loading & IGS" },
  { id: "opslog", label: "09 · Cargo Ops Log" },
  { id: "demurrage", label: "10 · Demurrage" },
  { id: "q88", label: "11 · Q88-OSV" },
  { id: "vetting", label: "12 · Vetting" },
  { id: "credentials", label: "13 · Coating & System" },
  { id: "sustain", label: "14 · Sustainability" },
  { id: "availability", label: "15 · Availability" },
  { id: "hse", label: "16 · HSE" },
  { id: "vi", label: "17 · Voyage Instructions" },
  { id: "supercargo", label: "18 · Supercargo" },
  { id: "library", label: "19 · Documents" },
  { id: "talk", label: "20 · Talk to Us" },
];

// Tabs that should display the interactive tank plan
const TABS_WITH_TANK_PLAN = new Set(["fleet", "compat", "ledger", "samples", "readiness", "opslog", "q88", "credentials"]);

const PortalChemCarriage = () => {
  const { companyName } = usePortalCompany();
  const charterer = companyName || "Charterer";
  const myVessels = useMemo(() => CHEM_VESSELS.slice(0, 2), []);
  const v0 = myVessels[0];
  const [activeTab, setActiveTab] = useState("welcome");
  const [selectedVesselImo, setSelectedVesselImo] = useState(myVessels[0].imo);
  const selectedVessel = myVessels.find(v => v.imo === selectedVesselImo) || myVessels[0];

  const TabHeader = ({ n, title, blurb }: { n: number; title: string; blurb?: string }) => (
    <div className="border-b border-border pb-3 mb-4">
      <p className="text-[10px] font-bold tracking-[2px] uppercase text-teal">Section {String(n).padStart(2, "0")}</p>
      <h2 className="text-xl font-bold mt-1">{title}</h2>
      {blurb && <p className="text-xs text-muted-foreground mt-1">{blurb}</p>}
    </div>
  );

  const TankPlanBlock = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-[11px] font-bold tracking-[2px] uppercase text-teal">Interactive Tank Plan</p>
        <Select value={selectedVesselImo} onValueChange={setSelectedVesselImo}>
          <SelectTrigger className="h-8 text-xs w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            {myVessels.map(v => (
              <SelectItem key={v.imo} value={v.imo}>{v.name} ({v.type})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <InteractiveTankPlan vessel={selectedVessel} />
    </div>
  );

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/70 mb-1">Operations · Chemical PSV Fleet</p>
        <h1 className="text-3xl font-bold">Chem Carriage</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Your live window into Velocity Offshore chemical operations — tank-by-tank provenance, parcel telemetry, and sampling chain of custody auditable to the molecule.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Horizontally scrollable tab strip */}
        <div className="border-b border-border overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-transparent h-auto p-0 inline-flex gap-1 w-max">
            {TABS.map(t => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className={cn(
                  "text-[11px] uppercase tracking-wider px-3 py-2 rounded-none border-b-2 border-transparent",
                  "data-[state=active]:border-teal data-[state=active]:text-teal data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                )}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="mt-6">
          {/* 01 Welcome */}
          <TabsContent value="welcome" className="space-y-4">
            <TabHeader n={1} title="Welcome & Capability Snapshot" />
            <div className="rounded-2xl bg-gradient-to-br from-[hsl(215,40%,15%)] via-[hsl(211,55%,20%)] to-[hsl(195,70%,25%)] p-7 text-white relative overflow-hidden">
              <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[hsl(160,80%,45%)]/15 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[hsl(160,90%,60%)]" />
                  <p className="text-[10px] font-bold tracking-[3px] uppercase text-white/70">Chemical PSV Operations</p>
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-2">Welcome, {charterer}</h2>
                <p className="text-sm text-white/70 max-w-2xl mb-5">
                  The only PSV operator delivering chemical parcels to chemical-tanker standards. Dual-vetted under OVMSA2 and CDI-M.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  {[
                    { l: "Vessels Available", v: myVessels.length },
                    { l: "Active Voyages", v: 1 },
                    { l: "Cargoes Delivered YTD", v: 14 },
                    { l: "Sampling Integrity", v: "100%" },
                  ].map(x => (
                    <div key={x.l} className="rounded-xl bg-white/5 backdrop-blur border border-white/10 p-4">
                      <p className="text-[10px] uppercase tracking-[2px] text-white/50">{x.l}</p>
                      <p className="font-display text-2xl font-bold mt-1">{x.v}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-0">OVMSA2 ✓</Badge>
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-0">CDI-M ✓</Badge>
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-0">OSV Chem Code ✓</Badge>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* 02 Fleet */}
          <TabsContent value="fleet" className="space-y-4">
            <TabHeader n={2} title="My Fleet" blurb="Vessels available under your commercial agreement. Type 1 = Stainless Steel 316L. Type 2 = Zinc Silicate." />
            <div className="grid md:grid-cols-2 gap-4">
              {myVessels.map(v => (
                <Card key={v.imo}><CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-display text-lg font-bold">{v.name}</p>
                      <p className="text-[11px] text-muted-foreground">IMO {v.imo} · {v.flag} · Built {v.yearBuilt}</p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <Badge variant="outline" className="text-[10px]">Under Charter</Badge>
                      <Badge className="text-[10px] bg-teal/15 text-teal border-0">{v.type}</Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                    <div><p className="text-[9px] uppercase text-muted-foreground">LOA</p><p className="font-medium">{v.loa} m</p></div>
                    <div><p className="text-[9px] uppercase text-muted-foreground">Cargo Cap.</p><p className="font-medium">{v.capacityM3} m³</p></div>
                    <div><p className="text-[9px] uppercase text-muted-foreground">Tanks</p><p className="font-medium">{v.tankCount}</p></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2"><span className="font-medium text-foreground">Tank coating:</span> {v.coatingType}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {v.annexCats.map(c => <Badge key={c} variant="secondary" className="text-[10px]">Annex II — {c}</Badge>)}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setSelectedVesselImo(v.imo); toast.success(`${v.name} selected`, { description: "Tank plan updated" }); }} size="sm" variant="outline" className="text-xs"><FlaskConical className="w-3 h-3 mr-1" />View Tank Plan</Button>
                    <Button onClick={() => toast.success("Generating: Q88-OSV PDF", { description: v.name })} size="sm" variant="outline" className="text-xs"><FileText className="w-3 h-3 mr-1" />Q88-OSV</Button>
                  </div>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>

          {/* 03 Voyages */}
          <TabsContent value="voyages" className="space-y-4">
            <TabHeader n={3} title="My Voyages" />
            <Tabs defaultValue="active">
              <TabsList><TabsTrigger value="active">Active</TabsTrigger><TabsTrigger value="upcoming">Upcoming</TabsTrigger><TabsTrigger value="completed">Completed</TabsTrigger></TabsList>
              <TabsContent value="active" className="mt-3 space-y-2">
                <Card><CardContent className="p-4 flex items-center justify-between text-xs">
                  <div>
                    <p className="font-bold">VOY-2026-014 · {v0.name}</p>
                    <p className="text-muted-foreground">Methanol 2,400 MT · Georgetown → Stabroek Block</p>
                  </div>
                  <Badge>Laden</Badge>
                </CardContent></Card>
              </TabsContent>
              <TabsContent value="upcoming" className="mt-3 text-xs text-muted-foreground">2 voyages on the horizon.</TabsContent>
              <TabsContent value="completed" className="mt-3 text-xs text-muted-foreground">14 completed YTD · zero contamination · 100% sampling integrity.</TabsContent>
            </Tabs>
          </TabsContent>

          {/* 04 Compatibility */}
          <TabsContent value="compat" className="space-y-4">
            <TabHeader n={4} title="Cargo Compatibility — Self-Service" blurb="Pre-qualify a cargo against our fleet. Use the tank plan below to see per-tank compatibility instantly." />
            <Card><CardContent className="p-5 grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Input placeholder="Cargo (e.g. Methanol)" />
                <Input placeholder="Quantity (MT)" />
                <Input placeholder="Loading Temp (°C)" />
                <Button onClick={() => toast.success("Compatibility check complete", { description: "See tank plan results below." })} className="w-full">Check Compatibility</Button>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <p className="font-bold text-sm">2 vessels compatible</p>
                </div>
                <ul className="text-xs space-y-1">
                  <li>✓ Velocity Aurora (Type 2 / Zn-Sil) — ready in 14 hr</li>
                  <li>✓ Velocity Triton (Type 1 / SS316L) — ready immediately</li>
                </ul>
                <Button onClick={() => toast.success("Indicative quote requested", { description: "Our team will respond shortly." })} size="sm" variant="outline" className="mt-3">Request Indicative Quote</Button>
              </div>
            </CardContent></Card>
          </TabsContent>

          {/* 05 L3C */}
          <TabsContent value="ledger" className="space-y-4">
            <TabHeader n={5} title="Tank History — Last 3 Cargoes" blurb="The single most important trust feature. Click any tank below for full L3C provenance." />
          </TabsContent>

          {/* 06 Tracking */}
          <TabsContent value="tracking" className="space-y-4">
            <TabHeader n={6} title="Live Parcel Tracking" />
            <Card><CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-bold text-sm">{v0.name} · VOY-2026-014</p>
                  <p className="text-xs text-muted-foreground"><MapPin className="inline w-3 h-3 mr-1" />{v0.location.port} · ETA {v0.eta}</p>
                </div>
                <Badge>Laden</Badge>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden mb-3">
                <div className="h-full w-[62%] bg-gradient-to-r from-teal to-emerald-500" />
              </div>
              <div className="grid grid-cols-4 gap-2 text-[10px] text-muted-foreground">
                <div className="text-emerald-600">✓ Departed LP</div>
                <div className="text-emerald-600">✓ Sea passage</div>
                <div className="font-bold text-foreground">→ Approach DP</div>
                <div>Hose connect</div>
              </div>
            </CardContent></Card>
          </TabsContent>

          {/* 07 Samples */}
          <TabsContent value="samples" className="space-y-4">
            <TabHeader n={7} title="Sampling & Chain of Custody — My Parcels" />
            <Card><CardContent className="p-0">
              <table className="w-full text-xs">
                <thead className="bg-muted text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Sample</th><th className="text-left p-3">Type</th><th className="text-left p-3">Witness</th><th className="text-left p-3">Status</th></tr>
                </thead>
                <tbody>
                  {CHEM_SAMPLES.map(s => (
                    <tr key={s.id} className="border-t border-border">
                      <td className="p-3 font-mono">{s.id}</td>
                      <td className="p-3">{s.type}</td>
                      <td className="p-3 text-muted-foreground">{s.witness}</td>
                      <td className="p-3"><Badge variant="outline" className="text-[10px]">{s.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
            <div className="flex gap-2">
              <Button onClick={() => toast.success("QR verification opened")} size="sm" variant="outline"><QrCode className="w-3 h-3 mr-1" />Verify QR</Button>
              <Button onClick={() => toast.success("Generating: Chain PDF")} size="sm" variant="outline"><Download className="w-3 h-3 mr-1" />Chain PDF</Button>
            </div>
          </TabsContent>

          {/* 08 Pre-Loading & IGS */}
          <TabsContent value="readiness" className="space-y-4">
            <TabHeader n={8} title="Pre-Loading Readiness & Inert Gas Status" blurb="Live IGS telemetry per vessel + readiness checklist. What you'd otherwise have to phone for." />

            {/* IGS panel — per vessel */}
            <div className="grid md:grid-cols-2 gap-3">
              {myVessels.map(v => {
                const igs = v.igs;
                const statusColor =
                  igs.status === "Green" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40" :
                  igs.status === "Amber" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40" :
                  "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40";
                return (
                  <Card key={v.imo}><CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-display font-bold text-sm flex items-center gap-2"><Wind className="w-4 h-4 text-teal" />{v.name} — IGS</p>
                        <p className="text-[11px] text-muted-foreground">{igs.system} · last test {igs.lastTest}</p>
                      </div>
                      <Badge className={cn("border", statusColor)}>{igs.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-[9px] uppercase text-muted-foreground">N₂ Purity</p>
                        <p className="font-display text-lg font-bold">{igs.purity}%</p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-[9px] uppercase text-muted-foreground">O₂ Content</p>
                        <p className="font-display text-lg font-bold">{igs.o2}%</p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-[9px] uppercase text-muted-foreground flex items-center gap-1"><Gauge className="w-3 h-3" />Pressure</p>
                        <p className="font-display text-lg font-bold">{igs.pressure} bar</p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-[9px] uppercase text-muted-foreground">Dew Point</p>
                        <p className="font-display text-lg font-bold">{igs.dewPoint}°C</p>
                      </div>
                    </div>
                    <Button onClick={() => toast.success("IGS log requested", { description: v.name })} size="sm" variant="outline" className="mt-3 text-xs"><Download className="w-3 h-3 mr-1" />IGS Log (24 hr)</Button>
                  </CardContent></Card>
                );
              })}
            </div>

            <Card><CardContent className="p-5 grid md:grid-cols-2 gap-3 text-xs">
              {[
                "Tank cleaning complete (2026-04-30)",
                "Wall-wash test passed (Saybolt 2026-05-01)",
                "Inert gas readiness confirmed",
                "Hose certificates current",
                "Manifold reducers staged for ANSI 6\"",
                "Crew rest hours compliant",
                "Drug & alcohol testing current",
                "Master's voyage orders issued",
              ].map(x => (
                <div key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />{x}</div>
              ))}
            </CardContent></Card>
          </TabsContent>

          {/* 09 Ops Log */}
          <TabsContent value="opslog" className="space-y-4">
            <TabHeader n={9} title="Cargo Operations Log (Live)" />
            <Card><CardContent className="p-4 text-xs space-y-1.5">
              {[
                "14:22Z — Hoses connected",
                "14:35Z — Lines blown",
                "14:58Z — Pre-transfer meeting complete",
                "15:10Z — IG on, pumps started",
                "16:02Z — First foot sample drawn",
                "16:18Z — Steady-state running, 480 m³/h",
              ].map(l => <div key={l} className="text-muted-foreground">{l}</div>)}
            </CardContent></Card>
          </TabsContent>

          {/* 10 Demurrage */}
          <TabsContent value="demurrage" className="space-y-4">
            <TabHeader n={10} title="Demurrage & Laytime — My Voyages" />
            <Card><CardContent className="p-5">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div><p className="text-[10px] uppercase text-muted-foreground">Laytime Used</p><p className="font-display text-xl font-bold">36h 14m</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Time Remaining</p><p className="font-display text-xl font-bold text-emerald-600">11h 46m</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Projected Exposure</p><p className="font-display text-xl font-bold">$0</p></div>
              </div>
              <Button onClick={() => toast.success("Generating: Laytime Calc PDF")} size="sm" variant="outline" className="mt-4"><Download className="w-3 h-3 mr-1" />Laytime Calc PDF</Button>
            </CardContent></Card>
          </TabsContent>

          {/* 11 Q88 */}
          <TabsContent value="q88" className="space-y-4">
            <TabHeader n={11} title="Vessel Particulars — Q88-OSV" blurb="The new industry standard for offshore chemical PSVs." />
            <div className="grid md:grid-cols-2 gap-3">
              {myVessels.map(v => (
                <Card key={v.imo}><CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm">{v.name}</p>
                    <p className="text-[11px] text-muted-foreground">IMO {v.imo} · {v.type} · {v.coatingType}</p>
                  </div>
                  <Button onClick={() => toast.success("Generating: Q88-OSV", { description: v.name })} size="sm"><Download className="w-3 h-3 mr-1" />Q88-OSV</Button>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>

          {/* 12 Vetting */}
          <TabsContent value="vetting" className="space-y-4">
            <TabHeader n={12} title="Vetting & Compliance Status" blurb="Currency dates only — findings detail withheld for confidentiality." />
            {CHEM_VETTING.slice(0, 2).map(r => (
              <Card key={r.vessel}><CardContent className="p-4 grid md:grid-cols-3 gap-3 text-xs">
                <div><p className="font-bold">{r.vessel}</p></div>
                <div className="flex items-center gap-2"><span className="text-muted-foreground">OVMSA2:</span> <Badge variant="outline">{r.ovmsa.status}</Badge> <span className="text-muted-foreground">next {r.ovmsa.next}</span></div>
                <div className="flex items-center gap-2"><span className="text-muted-foreground">CDI-M:</span> <Badge variant="outline">{r.cdim.status}</Badge> <span className="text-muted-foreground">next {r.cdim.next}</span></div>
              </CardContent></Card>
            ))}
          </TabsContent>

          {/* 13 Credentials */}
          <TabsContent value="credentials" className="space-y-4">
            <TabHeader n={13} title="Coating & Cargo System Credentials" />
            <Card><CardContent className="p-4 text-xs text-muted-foreground space-y-1">
              <p><span className="font-bold text-foreground">Type 1 vessels:</span> Stainless Steel 316L tanks — fully aggressive cargo capable.</p>
              <p><span className="font-bold text-foreground">Type 2 vessels:</span> Zinc Silicate coated tanks — wide industrial chemical envelope.</p>
              <p>International Paint NOA · Heating max 90°C · IGS N₂ PSA, 99.4%+ purity · Framo deepwell pumps with curves available.</p>
            </CardContent></Card>
          </TabsContent>

          {/* 14 Sustain */}
          <TabsContent value="sustain" className="space-y-4">
            <TabHeader n={14} title="Sustainability — My Voyages" />
            <div className="grid md:grid-cols-3 gap-3">
              {[{ l: "CO₂ on Your Cargo (YTD)", v: "412 t" }, { l: "gCO₂/parcel-t-mile", v: "6.8" }, { l: "EU ETS Allowances", v: "$8,400" }].map(k => (
                <Card key={k.l}><CardContent className="p-4">
                  <p className="text-[10px] uppercase text-muted-foreground">{k.l}</p>
                  <p className="font-display text-2xl font-bold">{k.v}</p>
                </CardContent></Card>
              ))}
            </div>
            <Button onClick={() => toast.success("Generating: Scope 3 / CSRD Pack")} size="sm" variant="outline"><Leaf className="w-3 h-3 mr-1" />Download Scope 3 / CSRD Pack</Button>
          </TabsContent>

          {/* 15 Availability */}
          <TabsContent value="availability" className="space-y-4">
            <TabHeader n={15} title="Vessel Availability Look-Ahead" />
            <Card><CardContent className="p-4 text-xs text-muted-foreground">Aurora open windows: 22-Jun → 04-Jul · 18-Aug → 02-Sep. Triton open: 11-Jun → 19-Jun.</CardContent></Card>
          </TabsContent>

          {/* 16 HSE */}
          <TabsContent value="hse" className="space-y-4">
            <TabHeader n={16} title="HSE Performance" blurb="Rolling 12-month transparency." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: "TRIR", v: CHEM_HSE_KPIS.trir },
                { l: "LTIF", v: CHEM_HSE_KPIS.ltif },
                { l: "Spills", v: CHEM_HSE_KPIS.spills },
                { l: "Contamination", v: CHEM_HSE_KPIS.contamination },
              ].map(k => (
                <Card key={k.l}><CardContent className="p-4 text-center">
                  <p className="text-[10px] uppercase text-muted-foreground">{k.l}</p>
                  <p className="font-display text-2xl font-bold text-emerald-600">{k.v}</p>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>

          {/* 17 VI */}
          <TabsContent value="vi" className="space-y-4">
            <TabHeader n={17} title="Voyage Instructions Intake" blurb="Replaces email-based handoff." />
            <Card><CardContent className="p-5 grid md:grid-cols-2 gap-3">
              <Input placeholder="Vessel" /><Input placeholder="Cargo + UN" /><Input placeholder="Loadport" /><Input placeholder="Discharge port" /><Input placeholder="Laycan" /><Input placeholder="Surveyor nomination" />
              <Button onClick={() => toast.success("Voyage Instructions submitted")} className="md:col-span-2">Submit Voyage Instructions</Button>
            </CardContent></Card>
          </TabsContent>

          {/* 18 Supercargo */}
          <TabsContent value="supercargo" className="space-y-4">
            <TabHeader n={18} title="Supercargo Embarkation" />
            <Card><CardContent className="p-5 grid md:grid-cols-2 gap-3">
              <Input placeholder="Rep name" /><Input placeholder="Passport #" /><Input placeholder="Embark port + date" /><Input placeholder="Disembark port + date" /><Input placeholder="PPE sizes" /><Input placeholder="Dietary requirements" />
              <Button onClick={() => toast.success("Submitted for ISPS Clearance")} className="md:col-span-2">Submit for ISPS Clearance</Button>
            </CardContent></Card>
          </TabsContent>

          {/* 19 Library */}
          <TabsContent value="library" className="space-y-4">
            <TabHeader n={19} title="Document Library" blurb="Watermarked, versioned, scoped to your account." />
            <div className="grid md:grid-cols-2 gap-2">
              {["Q88-OSV (current)", "Class certificates", "IOPP / IAPP / ISPP", "ISM DOC / SMC", "P&D Manual", "L3C Declaration", "Sampling Chain PDF", "Statement of Facts"].map(d => (
                <Card key={d}><CardContent className="p-3 flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" />{d}</span>
                  <Button onClick={() => toast.success(`Downloading: ${d}`)} size="sm" variant="ghost"><Download className="w-3 h-3" /></Button>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>

          {/* 20 Talk */}
          <TabsContent value="talk" className="space-y-4">
            <TabHeader n={20} title="Talk to Us" />
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { r: "Commercial / Chartering", n: "Bo Jardine — CCO" },
                { r: "Operations 24/7", n: "Shore Ops Desk" },
                { r: "Technical", n: "Technical Manager" },
                { r: "QHSE", n: "QHSE Manager" },
              ].map(p => (
                <Card key={p.r}><CardContent className="p-4 flex items-center justify-between">
                  <div><p className="text-[10px] uppercase text-muted-foreground">{p.r}</p><p className="font-bold text-sm">{p.n}</p></div>
                  <Button onClick={() => toast.success(`Message draft to ${p.n}`)} size="sm" variant="outline"><MessageSquare className="w-3 h-3 mr-1" />Message</Button>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>

          {/* Tank plan footer — appears on applicable tabs */}
          {TABS_WITH_TANK_PLAN.has(activeTab) && (
            <div className="mt-8">
              <TankPlanBlock />
            </div>
          )}
        </div>
      </Tabs>
    </PortalLayout>
  );
};

export default PortalChemCarriage;
